import React, { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  RiTruckLine,
  RiTeamLine,
  RiHistoryLine,
  RiToolsLine,
  RiDeleteBinLine,
  RiToggleLine,
  RiMailLine,
  RiStickyNoteLine,
} from 'react-icons/ri';
import supplierService from '../../services/supplierService';
import supplierRepairService from '../../services/supplierRepairService';
import { SupplierRepair, getStatusLabel } from '../../types/supplier-repair';
import { useDeleteSupplier, useToggleSupplierStatus } from '../../hooks/mutations/useSupplierMutations';
import { usePermission } from '../../hooks/usePermission';
import { queryKeys } from '../../queryClient';
import {
  SUPPLIERS_PATH,
  supplierEditUrl,
  supplierRepairDetailUrl,
  supplierRepairsBySupplierUrl,
} from '../../utils/supplierLinks';
import { parseRouteId } from '../../utils/routeId';
import { showConfirm } from '../../utils/alert';
import { formatDate } from '../../utils/dateUtils';
import { SUPPLIER_FIELD_LABELS } from '../../components/supplier/supplierForm';
import {
  DetailPageLayout,
  DetailSection,
  InfoField,
  InfoGrid,
  RecordMetaCard,
  EntityActivityTimeline,
  RelatedRecordsCard,
  useDetailNavigation,
  detailNavState,
  headerButtonClass,
  headerButtonVariants,
} from '../../components/detail';
import { cn } from '../../utils/cn';

const RELATED_LIMIT = 10;

/**
 * หน้ารายละเอียดผู้จำหน่าย — `/master-data/suppliers/:id` (แทนโมดัล "ดูรายละเอียดผู้จำหน่าย" · มติ PO 28 ต.ค. 2026)
 * ทุกช่องของฟอร์มเดิมแบบอ่านอย่างเดียว + การ์ด "งานส่งซ่อมของผู้จำหน่ายนี้" (gate supplier_repairs.view) +
 * ข้อมูลการสร้าง/แก้ไข + ประวัติกิจกรรม · action ด่วน (เปิด/ปิดใช้งาน · ลบ) ยังเป็น confirm แบบเดิม
 */
const SupplierViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const supplierId = parseRouteId(id);
  const location = useLocation();
  const { hasPermission } = usePermission();
  const { backTo, goTo, navState } = useDetailNavigation(SUPPLIERS_PATH);
  const deleteSupplierMutation = useDeleteSupplier();
  const toggleStatusMutation = useToggleSupplierStatus();
  const [activityRefresh, setActivityRefresh] = useState(0);

  const { data: supplier, isLoading, error, refetch } = useQuery({
    queryKey: [...queryKeys.suppliers.all, 'detail', supplierId ?? 0],
    queryFn: () => supplierService.getSupplierById(supplierId!),
    enabled: supplierId !== null,
  });

  const hereState = detailNavState(location);

  const handleRefresh = () => {
    refetch();
    setActivityRefresh((n) => n + 1);
  };

  const handleToggle = () => {
    if (!supplier) return;
    showConfirm(
      'ยืนยันการเปลี่ยนสถานะ',
      `คุณต้องการ${supplier.is_active ? 'ปิด' : 'เปิด'}ใช้งานผู้จำหน่าย "${supplier.name}" ใช่หรือไม่?`,
      async () => {
        try {
          await toggleStatusMutation.mutateAsync(supplier.id);
          handleRefresh();
        } catch (e) {
          console.error('Error toggling supplier:', e); // useMutationWithRefresh แสดงข้อความแล้ว
        }
      },
      { confirmText: 'ยืนยัน', cancelText: 'ยกเลิก' }
    );
  };

  const handleDelete = () => {
    if (!supplier) return;
    showConfirm(
      'ยืนยันการลบ',
      `คุณต้องการลบผู้จำหน่าย "${supplier.name}" ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้`,
      async () => {
        try {
          await deleteSupplierMutation.mutateAsync(supplier.id);
          goTo(backTo, { replace: true });
        } catch (e) {
          // useMutationWithRefresh แสดงข้อความจาก server แล้ว (เช่น มีใบส่งซ่อม/อุปกรณ์ผูกอยู่)
          console.error('Error deleting supplier:', e);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  const notFound = supplierId === null ? { response: { status: 404 } } : null;
  const isActive = supplier?.is_active ?? true;

  return (
    <DetailPageLayout
      breadcrumbs={[{ label: 'ซัพพลายเออร์', path: backTo }, { label: supplier?.name || 'รายละเอียด' }]}
      icon={<RiTruckLine />}
      title={supplier?.name || 'รายละเอียดผู้จำหน่าย'}
      recordId={supplier?.code}
      subtitle="รายละเอียดซัพพลายเออร์ / ผู้จำหน่าย"
      backTo={backTo}
      onRefresh={handleRefresh}
      editTo={supplier ? supplierEditUrl(supplier.id) : undefined}
      editPermission="suppliers.edit"
      editState={navState}
      actions={
        <>
          {supplier && hasPermission('suppliers.edit') && (
            <button
              type="button"
              onClick={handleToggle}
              disabled={toggleStatusMutation.isPending}
              className={cn(headerButtonClass, headerButtonVariants.neutral, 'disabled:opacity-50')}
            >
              <RiToggleLine className="h-4 w-4 mr-1.5" />
              {supplier.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
            </button>
          )}
          {supplier && hasPermission('suppliers.delete') && (
            <button type="button" onClick={handleDelete} className={cn(headerButtonClass, headerButtonVariants.danger)}>
              <RiDeleteBinLine className="h-4 w-4 mr-1.5" />
              ลบ
            </button>
          )}
        </>
      }
      loading={supplierId !== null && isLoading}
      error={notFound || error}
      notFoundMessage="ไม่พบผู้จำหน่ายที่ระบุ (อาจถูกลบไปแล้ว)"
      onRetry={() => refetch()}
      sidebar={
        supplier && (
          <>
            <RecordMetaCard
              createdAt={supplier.created_at}
              createdBy={supplier.created_by}
              updatedAt={supplier.updated_at}
              updatedBy={supplier.updated_by}
            />
            <DetailSection title="ประวัติกิจกรรม" icon={<RiHistoryLine />}>
              <EntityActivityTimeline
                entityType="supplier"
                entityId={supplier.id}
                refreshTrigger={activityRefresh}
                fieldLabels={SUPPLIER_FIELD_LABELS}
                valueFormatters={{ is_active: (v) => (v ? 'ใช้งาน' : 'ไม่ใช้งาน') }}
              />
            </DetailSection>
          </>
        )
      }
    >
      {supplier && (
        <>
          <DetailSection
            title="ข้อมูลพื้นฐาน"
            icon={<RiTruckLine />}
            actions={
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border',
                  isActive ? 'bg-success-50 text-success-700 border-success-200' : 'bg-danger-50 text-danger-700 border-danger-200'
                )}
                data-testid="supplier-status-badge"
              >
                <span className={cn('w-1.5 h-1.5 rounded-full', isActive ? 'bg-success-500' : 'bg-danger-500')} />
                {isActive ? 'ใช้งาน' : 'ไม่ใช้งาน'}
              </span>
            }
          >
            <InfoGrid>
              <InfoField label="รหัสผู้จำหน่าย" value={supplier.code} mono />
              <InfoField label="ชื่อผู้จำหน่าย" value={supplier.name} />
              <InfoField label="เลขประจำตัวผู้เสียภาษี" value={supplier.tax_id} mono />
              <InfoField label="ที่อยู่" value={supplier.address} multiline fullWidth />
            </InfoGrid>
          </DetailSection>

          <DetailSection title="ข้อมูลติดต่อ" icon={<RiTeamLine />}>
            <InfoGrid>
              <InfoField label="ผู้ติดต่อ" value={supplier.contact_person} />
              <InfoField label="เบอร์โทรศัพท์" value={supplier.phone} mono />
              <InfoField label="อีเมล" value={supplier.email}>
                {supplier.email ? (
                  <a href={`mailto:${supplier.email}`} className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 [overflow-wrap:anywhere]">
                    <RiMailLine className="h-4 w-4 flex-shrink-0" />
                    {supplier.email}
                  </a>
                ) : undefined}
              </InfoField>
            </InfoGrid>
          </DetailSection>

          <DetailSection title="หมายเหตุ" icon={<RiStickyNoteLine />}>
            <InfoGrid columns={1}>
              <InfoField label="หมายเหตุ" value={supplier.notes} multiline />
            </InfoGrid>
          </DetailSection>

          <RelatedRecordsCard<SupplierRepair>
            title="งานส่งซ่อมของผู้จำหน่ายนี้"
            icon={<RiToolsLine />}
            permission="supplier_repairs.view"
            queryKey={['supplier-related', supplier.id, 'supplier-repairs']}
            fetcher={() => supplierRepairService.getSupplierRepairs({ supplier_id: supplier.id, limit: RELATED_LIMIT })}
            rowKey={(r) => r.id}
            rowTo={(r) => supplierRepairDetailUrl(r.id)}
            rowState={hereState}
            viewAllTo={supplierRepairsBySupplierUrl(supplier.id)}
            emptyText="ยังไม่มีงานส่งซ่อมกับผู้จำหน่ายนี้"
            testId="supplier-related-repairs"
            renderRow={(r) => {
              const deviceCount = r.items && r.items.length > 0 ? r.items.length : r.device ? 1 : 0;
              const firstCode = r.items?.[0]?.device?.code || r.device?.code;
              return (
                <>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-secondary-900 font-mono">{r.request_number}</p>
                    <p className="text-xs text-secondary-500 [overflow-wrap:anywhere]">
                      {firstCode || '-'}
                      {deviceCount > 1 ? ` +${deviceCount - 1} เครื่อง` : ''}
                      {r.sent_date ? ` · ส่ง ${formatDate(r.sent_date)}` : ''}
                    </p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-xs flex-shrink-0 bg-secondary-100 text-secondary-700">
                    {getStatusLabel(r.status)}
                  </span>
                </>
              );
            }}
          />
        </>
      )}
    </DetailPageLayout>
  );
};

export default SupplierViewPage;
